import * as React from 'react';
import { IconButton } from '@material-ui/core';
import styled from 'styled-components';

import DescriptionIcon from '@material-ui/icons/Description';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import ReceiptIcon from '@material-ui/icons/Receipt';
import NoteIcon from '@material-ui/icons/Note';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import FolderIcon from '@material-ui/icons/Folder';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import HealingIcon from '@material-ui/icons/Healing';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import StarIcon from '@material-ui/icons/Star';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import LocalPharmacyIcon from '@material-ui/icons/LocalPharmacy';
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import FitnessCenterIcon from '@material-ui/icons/FitnessCenter';
import ChildCareIcon from '@material-ui/icons/ChildCare';
import GavelIcon from '@material-ui/icons/Gavel';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import ColorizeIcon from '@material-ui/icons/Colorize';
import AccessibilityNewIcon from '@material-ui/icons/AccessibilityNew';
import ScannerIcon from '@material-ui/icons/Scanner';
import SpaIcon from '@material-ui/icons/Spa';
import ImageIcon from '@material-ui/icons/Image';
import MoodIcon from '@material-ui/icons/Mood';
import OpacityIcon from '@material-ui/icons/Opacity';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import DirectionsRunIcon from '@material-ui/icons/DirectionsRun';
import WcIcon from '@material-ui/icons/Wc';
import PregnantWomanIcon from '@material-ui/icons/PregnantWoman';

export const DOCUMENT_ICONS: { key: string; component: React.ComponentType<{ style?: React.CSSProperties }> }[] = [
    // Documents
    { key: 'description', component: DescriptionIcon },
    { key: 'assignment', component: AssignmentIcon },
    { key: 'assignmentInd', component: AssignmentIndIcon },
    { key: 'note', component: NoteIcon },
    { key: 'insertDriveFile', component: InsertDriveFileIcon },
    { key: 'folder', component: FolderIcon },
    { key: 'libraryBooks', component: LibraryBooksIcon },
    { key: 'receipt', component: ReceiptIcon },
    { key: 'verifiedUser', component: VerifiedUserIcon },
    { key: 'gavel', component: GavelIcon },
    // Medical - general
    { key: 'localHospital', component: LocalHospitalIcon },
    { key: 'healing', component: HealingIcon },
    { key: 'localPharmacy', component: LocalPharmacyIcon },
    { key: 'colorize', component: ColorizeIcon },       // lab / blood tests
    { key: 'opacity', component: OpacityIcon },          // blood / fluids
    { key: 'scanner', component: ScannerIcon },          // imaging / x-ray / bone
    { key: 'image', component: ImageIcon },              // x-ray / scan photo
    // Cardiology
    { key: 'favorite', component: FavoriteIcon },
    { key: 'favoriteBorder', component: FavoriteBorderIcon },
    // Dentist / face
    { key: 'mood', component: MoodIcon },                // dental / smile
    // Ophthalmology
    { key: 'removeRedEye', component: RemoveRedEyeIcon },
    // Physio / sports
    { key: 'fitnessCenter', component: FitnessCenterIcon },
    { key: 'accessibilityNew', component: AccessibilityNewIcon },
    { key: 'directionsRun', component: DirectionsRunIcon },
    // Wellness / nutrition
    { key: 'spa', component: SpaIcon },
    // Paediatrics / obstetrics
    { key: 'childCare', component: ChildCareIcon },
    { key: 'pregnantWoman', component: PregnantWomanIcon },
    { key: 'wc', component: WcIcon },
    // Misc
    { key: 'star', component: StarIcon },
    { key: 'bookmark', component: BookmarkIcon },
];

export const COLOR_OPTIONS = [
    // Blues
    '#1b6ec2',
    '#03a9f4',
    '#3f51b5',
    '#673ab7',
    // Greens
    '#00b07f',
    '#4caf50',
    '#009688',
    '#8bc34a',
    // Reds / pinks
    '#f44336',
    '#b71c1c',
    '#e91e63',
    '#f48fb1',
    // Oranges / yellows
    '#ff9800',
    '#ff5722',
    '#ffc107',
    '#ff8f00',
    // Neutrals
    '#607d8b',
    '#90a4ae',
    '#795548',
    '#9e9e9e',
    // Cyan / teal
    '#00bcd4',
    '#26a69a',
    // Purple
    '#9c27b0',
    '#ab47bc',
];

export function getIconComponent(iconKey: string | undefined): React.ComponentType<{ style?: React.CSSProperties }> | null {
    if (!iconKey) return null;
    return DOCUMENT_ICONS.find(i => i.key === iconKey)?.component ?? null;
}

export const PickerButton = styled(IconButton)<{ $color?: string; $selected?: boolean }>`
    && {
        border: var(--border-thin) solid var(--appLigthGrey);
        border-radius: var(--spacer-XS);
        padding: var(--spacer-XS);
        color: ${({ $color }) => $color || 'var(--text-body)'};
        background-color: ${({ $selected }) => $selected ? 'var(--bg-hover-light)' : 'transparent'};
    }
`;

export const PopoverContent = styled.div`
    padding: var(--spacer-S);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

export const PopoverTitle = styled.div`
    font-size: 0.8rem;
    color: var(--text-muted);
    font-weight: 500;
`;

export const IconGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: var(--spacer-XS);
`;

export const IconOptionButton = styled(IconButton)<{ $selected?: boolean; $color?: string }>`
    && {
        border-radius: var(--spacer-XS);
        padding: var(--spacer-XS);
        color: ${({ $color }) => $color || 'var(--text-body)'};
        background-color: ${({ $selected }) => $selected ? 'var(--bg-hover-light)' : 'transparent'};
        border: var(--border-thin) solid ${({ $selected }) => $selected ? 'var(--appBlue)' : 'transparent'};
    }
`;

export const ColorGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: var(--spacer-XS);
`;

interface ColorSwatchProps {
    $color: string;
    $selected?: boolean;
    onClick?: () => void;
    title?: string;
}

export const ColorSwatch = styled.button<ColorSwatchProps>`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    border: var(--border-medium) solid ${({ $selected }) => $selected ? 'var(--text-body)' : 'transparent'};
    cursor: pointer;
    outline: none;
    &:focus {
        outline: none;
    }
`;

interface ClearButtonProps {
    onClick?: () => void;
    children?: React.ReactNode;
}

export const ClearButton = styled.button<ClearButtonProps>`
    background: none;
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    padding: var(--spacer-XXS) var(--spacer-XS);
    cursor: pointer;
    font-size: 0.75rem;
    color: var(--text-muted);
`;
